'use client';
import isEmpty from 'lodash.isempty';
import noop from 'lodash.noop';
import React, { useMemo, useContext, createContext, useState, useCallback, isValidElement } from 'react';
import ToastAction from '../components/toast/ToastAction.mjs';
import ToastNotice from '../components/toast/ToastNotice.mjs';
import { FIXED_DURATION, DEFAULT_DURATION } from '../constants/toast.mjs';

const ToastContext = /*#__PURE__*/createContext({});
const isReactNode = node => /*#__PURE__*/isValidElement(node) || typeof node === 'string' || typeof node === 'number' || typeof node === 'boolean' || !node;
const useControl = function () {
  let defaultParams = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const [params, setParams] = useState({});
  const [duration, setDuration] = useState(FIXED_DURATION);
  const open = useCallback(nextParams => {
    const nextParamsDuration = nextParams === null || nextParams === void 0 ? void 0 : nextParams.duration;
    const defaultDuration = defaultParams.duration;
    setParams(isReactNode(nextParams) ? {
      text: nextParams,
      onClose: noop,
      ...(defaultDuration !== undefined ? {
        duration: defaultDuration
      } : {})
    } : nextParams);
    setDuration(nextParamsDuration || defaultDuration || FIXED_DURATION);
  }, [defaultParams.duration]);
  const close = useCallback(() => {
    setDuration(0);
  }, []);
  const onDestroy = useCallback(() => {
    const {
      onClose
    } = params;
    typeof onClose === 'function' && onClose();
    setParams({});
    setDuration(FIXED_DURATION);
  }, [params]);
  return {
    open,
    close,
    onDestroy,
    params,
    duration
  };
};
function ToastProvider(_ref) {
  let {
    children,
    duration = DEFAULT_DURATION
  } = _ref;
  const {
    params: actionParams,
    onDestroy: onActionDestroy,
    duration: actionDuration,
    ...action
  } = useControl();
  const {
    params: noticeParams,
    onDestroy: onNoticeDestroy,
    duration: noticeDuration,
    ...notice
  } = useControl({
    duration
  });
  const {
    text: actionText,
    imageComponent,
    bottomPaddingBase: actionBottomPaddingBase
  } = actionParams;
  const {
    text: noticeText,
    rightButton,
    bottomPaddingBase: noticeBottomPaddingBase
  } = noticeParams;
  const contextValue = useMemo(() => ({
    action: {
      isShow: !!actionText,
      ...action
    },
    notice: {
      isShow: !!noticeText,
      ...notice
    }
  }), [action, actionText, notice, noticeText]);
  return /*#__PURE__*/React.createElement(ToastContext.Provider, {
    value: contextValue
  }, children, /*#__PURE__*/React.createElement(ToastAction, {
    onDestroy: onActionDestroy,
    duration: actionDuration,
    imageComponent: imageComponent,
    onClickCloseButton: (actionParams === null || actionParams === void 0 ? void 0 : actionParams.onClickCloseButton) ?? action.close,
    bottomPaddingBase: actionBottomPaddingBase
  }, actionText), /*#__PURE__*/React.createElement(ToastNotice, {
    onDestroy: onNoticeDestroy,
    duration: noticeDuration,
    rightButton: rightButton,
    bottomPaddingBase: noticeBottomPaddingBase
  }, noticeText));
}
/**
 * @see https://oss.fin.navercorp.com/pages/finance-fe/deFign/v7?path=/story/ui_defign-ui-toast--toast-notice
 */
const useToastNotice = () => {
  const context = useContext(ToastContext);
  if (isEmpty(context)) {
    throw new Error('[deFign/Toast] Could not use Toast. You should wrap DefignProvider in root component.');
  }
  const {
    notice: {
      isShow,
      open,
      close
    }
  } = context;
  return {
    isShow,
    open,
    close
  };
};
/**
 * @see https://oss.fin.navercorp.com/pages/finance-fe/deFign/v7?path=/story/ui_defign-ui-toast--toast-action
 */
const useToastAction = () => {
  const context = useContext(ToastContext);
  if (isEmpty(context)) {
    throw new Error('[deFign/Toast] Could not use Toast. You should wrap DefignProvider in root component.');
  }
  const {
    action: {
      isShow,
      open,
      close
    }
  } = context;
  return {
    isShow,
    open,
    close
  };
};

export { ToastProvider, useToastAction, useToastNotice };
